import { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { RotateCcw, Bot, User, Hand, Scissors } from "lucide-react";
import axios from "axios";
import { useLang, STR } from "@/i18n";
import { sfx } from "@/sound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RockIcon = ({ className, strokeWidth = 2 }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M3.5 14.5 5 8.5 9.5 4.8 15.2 5.3 19.8 9.4 20.5 14.3 16.8 18.8 8.6 19.2Z" />
    <path d="M5 8.5 11 10.8 15.2 5.3" />
    <path d="M11 10.8 9.2 19" />
  </svg>
);

const MOVES = [
  { id: "pedra", icon: RockIcon, cls: "text-sky-300 border-sky-400/30 bg-sky-400/10" },
  { id: "papel", icon: Hand, cls: "text-amber-300 border-amber-400/30 bg-amber-400/10" },
  { id: "tesoura", icon: Scissors, cls: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" },
];
const BEATS = { pedra: "tesoura", tesoura: "papel", papel: "pedra" };
const meta = (id) => MOVES.find((m) => m.id === id);

export default function PedraPapelTesoura() {
  const { lang } = useLang();
  const t = STR[lang];
  const [phase, setPhase] = useState("choose");
  const [myMove, setMyMove] = useState(null);
  const [aiMove, setAiMove] = useState(null);
  const [score, setScore] = useState({ you: 0, ai: 0, d: 0 });
  const timer = useRef(null);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [12, -12]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-14, 14]), { stiffness: 120, damping: 16 });

  useEffect(() => () => clearTimeout(timer.current), []);

  const outcome =
    myMove && aiMove ? (myMove === aiMove ? "draw" : BEATS[myMove] === aiMove ? "win" : "lose") : null;

  const play = (move) => {
    if (phase !== "choose") return;
    const ai = MOVES[Math.floor(Math.random() * 3)].id;
    setMyMove(move);
    setAiMove(ai);
    setPhase("reveal");
    sfx.move();
    const res = move === ai ? "d" : BEATS[move] === ai ? "you" : "ai";
    if (res === "you") sfx.win();
    else if (res === "ai") sfx.lose();
    else sfx.draw();
    axios.post(`${API}/score`, { result: res === "d" ? "draw" : res }).catch(() => {});
    setScore((s) => ({ ...s, [res]: s[res] + 1 }));
    timer.current = setTimeout(() => {
      setMyMove(null);
      setAiMove(null);
      setPhase("choose");
    }, 2600);
  };

  const reset = () => {
    clearTimeout(timer.current);
    setPhase("choose");
    setMyMove(null);
    setAiMove(null);
    setScore({ you: 0, ai: 0, d: 0 });
  };

  const moveName = (id) => t[`rps_${id}`];

  const status =
    phase === "choose"
      ? { text: t.rps_choose, cls: "text-slate-300" }
      : outcome === "win"
        ? { text: t.st_win, cls: "text-sky-300" }
        : outcome === "draw"
          ? { text: t.st_draw, cls: "text-slate-300" }
          : { text: t.st_lose, cls: "text-amber-300" };

  return (
    <div
      data-testid="rps-game"
      className="relative"
      style={{ perspective: 1200 }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
      }}
      onMouseLeave={() => {
        mx.set(0.5);
        my.set(0.5);
      }}
    >
      <div className="absolute -inset-10 bg-[radial-gradient(circle_at_50%_40%,rgba(46,107,255,0.18),rgba(251,191,36,0.1)_45%,transparent_70%)] blur-2xl pointer-events-none" />

      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative">
        <div className="rounded-[2rem] border border-white/10 bg-[#0c1122]/90 backdrop-blur-xl p-5 sm:p-7 shadow-[0_40px_120px_-20px_rgba(46,107,255,0.35)]">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <span className="grid place-items-center w-8 h-8 rounded-lg bg-sky-400/15 border border-sky-400/30">
                <User className="w-4 h-4 text-sky-300" />
              </span>
              <div>
                <p className="text-xs font-bold text-white leading-none">{t.you}</p>
                <p className="mono text-[10px] text-sky-300 mt-1">{score.you} {t.wins}</p>
              </div>
            </div>
            <span className="mono text-[10px] uppercase tracking-[0.3em] text-slate-500">vs</span>
            <div className="flex items-center gap-2">
              <div className="text-right">
                <p className="text-xs font-bold text-white leading-none">DUELO IA</p>
                <p className="mono text-[10px] text-amber-300 mt-1">{score.ai} {t.wins}</p>
              </div>
              <span className="grid place-items-center w-8 h-8 rounded-lg bg-amber-400/15 border border-amber-400/30">
                <Bot className="w-4 h-4 text-amber-300" />
              </span>
            </div>
          </div>

          <div className="relative h-[290px] sm:h-[320px]">
            <AnimatePresence mode="wait">
              {phase === "choose" && (
                <motion.div
                  key="choose"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-7"
                >
                  <p className="mono text-[11px] uppercase tracking-[0.25em] text-slate-400">
                    {t.rps_choose}
                  </p>
                  <div className="grid grid-cols-3 gap-3 sm:gap-4 w-full max-w-[340px]">
                    {MOVES.map((m) => (
                      <motion.button
                        key={m.id}
                        data-testid={`rps-move-${m.id}`}
                        onClick={() => play(m.id)}
                        whileHover={{ scale: 1.08, y: -4 }}
                        whileTap={{ scale: 0.9 }}
                        className={`flex flex-col items-center gap-2.5 rounded-3xl border py-6 transition-colors duration-200 ${m.cls}`}
                      >
                        <m.icon className="w-8 h-8" strokeWidth={2} />
                        <span className="mono text-[10px] uppercase tracking-widest font-bold">
                          {moveName(m.id)}
                        </span>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}

              {phase === "reveal" && myMove && aiMove && (
                <motion.div
                  key="reveal"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-5"
                >
                  <div className="flex items-center gap-4 sm:gap-6">
                    {[
                      { mv: meta(myMove), label: t.you, delay: 0, rot: -12 },
                      { mv: meta(aiMove), label: "IA", delay: 0.25, rot: 12 },
                    ].map(({ mv, label, delay, rot }, i) => (
                      <div key={label} className="text-center flex items-center gap-4 sm:gap-6">
                        {i === 1 && (
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="font-display font-black text-xl text-slate-500"
                          >
                            ×
                          </motion.span>
                        )}
                        <div>
                          <motion.div
                            initial={{ scale: 0, rotate: rot }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ type: "spring", stiffness: 300, damping: 15, delay }}
                            className={`w-20 h-20 sm:w-24 sm:h-24 rounded-3xl border grid place-items-center ${mv.cls}`}
                          >
                            <mv.icon className="w-10 h-10 sm:w-12 sm:h-12" strokeWidth={1.8} />
                          </motion.div>
                          <p className="mono text-[10px] uppercase tracking-widest text-slate-400 mt-2">
                            {label} · {moveName(mv.id)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <motion.p
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.7, type: "spring", stiffness: 260, damping: 16 }}
                    className={`font-display font-black text-xl sm:text-2xl text-center ${
                      outcome === "win" ? "text-sky-300" : outcome === "draw" ? "text-slate-300" : "text-amber-300"
                    }`}
                  >
                    {outcome === "win" ? t.st_win : outcome === "draw" ? t.st_draw : t.st_lose}
                  </motion.p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="mt-5 flex items-center justify-between">
            <p data-testid="rps-status" className={`mono text-[11px] uppercase tracking-[0.15em] ${status.cls}`}>
              {status.text}
            </p>
            <button
              onClick={reset}
              data-testid="rps-reset-btn"
              className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-slate-400 hover:text-amber-300 hover:border-amber-400/40 hover:rotate-[-120deg] transition-all duration-300"
              aria-label="Reiniciar partida"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div
          className="animate-float-slow absolute -top-6 -right-4 sm:-right-8 rounded-2xl border border-amber-400/30 bg-[#141b33]/95 backdrop-blur px-4 py-2.5 shadow-xl"
          style={{ "--float-rot": "4deg", transform: "translateZ(60px)" }}
        >
          <p className="mono text-[10px] uppercase tracking-widest text-amber-300">{t.in_dispute}</p>
          <p className="font-display font-bold text-white text-sm">{t.credits10}</p>
        </div>
        <div
          className="animate-float-slow absolute -bottom-6 -left-4 sm:-left-8 rounded-2xl border border-sky-400/30 bg-[#141b33]/95 backdrop-blur px-4 py-2.5 shadow-xl"
          style={{ "--float-rot": "-5deg", animationDelay: "1.2s", transform: "translateZ(60px)" }}
        >
          <p className="mono text-[10px] uppercase tracking-widest text-sky-300">{t.pi_round}</p>
          <p className="font-display font-bold text-white text-sm">{t.pi_flash}</p>
        </div>
      </motion.div>
    </div>
  );
}
